/**
 * CL-DESIGN-BOARDS-DB-1A — Design Boards schema constants and record validation (pure).
 */

import { isValidProjectProductPathId } from "./projectProductPaths";

export const PROJECT_DESIGN_BOARDS_SUBCOLLECTION = "projectDesignBoards";
export const PROJECT_DESIGN_BOARD_SECTIONS_SUBCOLLECTION = "sections";
export const PROJECT_DESIGN_BOARD_ITEMS_SUBCOLLECTION = "items";
export const PROJECT_DESIGN_BOARD_ITEM_COMMENTS_SUBCOLLECTION = "comments";
export const PROJECT_DESIGN_BOARD_FILE_ACCESS_SUBCOLLECTION = "projectDesignBoardFileAccess";

export const DESIGN_BOARD_SCHEMA_VERSION = 1;

export const DESIGN_BOARD_STATUS = Object.freeze({
  DRAFT: "draft",
  SHARED: "shared",
  ARCHIVED: "archived",
});

export const DESIGN_BOARD_SECTION_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const DESIGN_BOARD_ITEM_TYPE = Object.freeze({
  FILE_REF: "file_ref",
  PRODUCT_OPTION_REF: "product_option_ref",
  INSPIRATION: "inspiration",
  SELECTION_LINK: "selection_link",
});

export const DESIGN_BOARD_COMMENT_STATUS = Object.freeze({
  ACTIVE: "active",
  HIDDEN: "hidden",
});

export const DESIGN_BOARD_FILE_ACCESS_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

/** Max file access docs updated in one trusted mutation (aligned with publication write caps). */
export const MAX_DESIGN_BOARD_FILE_ACCESS_WRITES_PER_MUTATION = 100;

/** Max items scanned when recomputing membership for one board mutation. */
export const MAX_DESIGN_BOARD_ITEMS_SCAN_PER_MUTATION = 500;

function safeStr(value) {
  return String(value ?? "").trim();
}

export function designBoardStatusAllowsClientList(status = "") {
  return safeStr(status) === DESIGN_BOARD_STATUS.SHARED;
}

export function designBoardEntitlesFileAccess(board = {}) {
  return (
    safeStr(board.status) === DESIGN_BOARD_STATUS.SHARED
    && board.clientVisible === true
  );
}

export function normalizeDesignBoardPermissions(permissions = {}) {
  const p = permissions && typeof permissions === "object" ? permissions : {};
  return {
    canViewDesignBoards: p.canViewDesignBoards === true,
    canManageDesignBoards: p.canManageDesignBoards === true,
    canContributeDesignBoardItems: p.canContributeDesignBoardItems === true,
    canCommentOnDesignBoards: p.canCommentOnDesignBoards === true,
    canViewSuppliersAndSelections: p.canViewSuppliersAndSelections === true,
  };
}

export function validateDesignBoardRecord(board = {}, { projectId = "" } = {}) {
  const pid = safeStr(projectId || board.projectId);
  const boardId = safeStr(board.boardId);
  const title = safeStr(board.title);
  const status = safeStr(board.status);
  if (!pid || !isValidProjectProductPathId(pid)) {
    return { ok: false, error: "Invalid project id." };
  }
  if (!boardId || !isValidProjectProductPathId(boardId)) {
    return { ok: false, error: "Invalid board id." };
  }
  if (!title) return { ok: false, error: "Board title is required." };
  if (!Object.values(DESIGN_BOARD_STATUS).includes(status)) {
    return { ok: false, error: "Invalid board status." };
  }
  const areaId = safeStr(board.areaId);
  if (areaId && !isValidProjectProductPathId(areaId)) {
    return { ok: false, error: "Invalid area id." };
  }
  return { ok: true };
}

export function validateDesignBoardSectionRecord(section = {}) {
  const sectionId = safeStr(section.sectionId);
  const title = safeStr(section.title);
  const status = safeStr(section.status || DESIGN_BOARD_SECTION_STATUS.ACTIVE);
  if (!sectionId || !isValidProjectProductPathId(sectionId)) {
    return { ok: false, error: "Invalid section id." };
  }
  if (!title) return { ok: false, error: "Section title is required." };
  if (!Object.values(DESIGN_BOARD_SECTION_STATUS).includes(status)) {
    return { ok: false, error: "Invalid section status." };
  }
  return { ok: true };
}

export function validateDesignBoardItemRecord(item = {}) {
  const itemId = safeStr(item.itemId);
  const sectionId = safeStr(item.sectionId);
  const itemType = safeStr(item.itemType);
  if (!itemId || !isValidProjectProductPathId(itemId)) {
    return { ok: false, error: "Invalid item id." };
  }
  if (!sectionId || !isValidProjectProductPathId(sectionId)) {
    return { ok: false, error: "Invalid section id." };
  }
  if (!Object.values(DESIGN_BOARD_ITEM_TYPE).includes(itemType)) {
    return { ok: false, error: "Invalid item type." };
  }
  if (itemType === DESIGN_BOARD_ITEM_TYPE.FILE_REF) {
    const fileId = safeStr(item.fileId);
    if (!fileId || !isValidProjectProductPathId(fileId)) {
      return { ok: false, error: "file_ref requires fileId." };
    }
  }
  if (
    itemType === DESIGN_BOARD_ITEM_TYPE.PRODUCT_OPTION_REF
    || itemType === DESIGN_BOARD_ITEM_TYPE.SELECTION_LINK
  ) {
    const requirementId = safeStr(item.requirementId);
    const optionId = safeStr(item.optionId);
    if (!requirementId || !optionId) {
      return { ok: false, error: "Product link requires requirementId and optionId." };
    }
  }
  if (itemType === DESIGN_BOARD_ITEM_TYPE.INSPIRATION) {
    if (!safeStr(item.title)) {
      return { ok: false, error: "Inspiration requires title." };
    }
  }
  return { ok: true };
}

/**
 * Firestore list constraints (parent path carries boardId; items do not store boardId).
 */
export const DESIGN_BOARD_CLIENT_LIST_QUERY = Object.freeze({
  collection: PROJECT_DESIGN_BOARDS_SUBCOLLECTION,
  filters: [
    { field: "status", op: "==", value: DESIGN_BOARD_STATUS.SHARED },
    { field: "clientVisible", op: "==", value: true },
  ],
});

export const DESIGN_BOARD_ITEMS_LIST_QUERY = Object.freeze({
  collection: PROJECT_DESIGN_BOARD_ITEMS_SUBCOLLECTION,
  filters: [{ field: "sectionId", op: "==", value: "<sectionId>" }],
  orderBy: [{ field: "sortOrder", direction: "asc" }],
});

export const DESIGN_BOARD_COMMENTS_LIST_QUERY = Object.freeze({
  collection: PROJECT_DESIGN_BOARD_ITEM_COMMENTS_SUBCOLLECTION,
  filters: [{ field: "status", op: "==", value: DESIGN_BOARD_COMMENT_STATUS.ACTIVE }],
});

/**
 * Room label for board-only viewers: use denorm on board; never requires projectProductAreas read.
 */
export function resolveDesignBoardAreaLabel(board = {}, { areas = null, canReadProductAreas = false } = {}) {
  const denorm = safeStr(board.areaDisplayName);
  if (denorm) return denorm;
  if (!canReadProductAreas || !Array.isArray(areas)) return "";
  const areaId = safeStr(board.areaId);
  if (!areaId) return "";
  const match = areas.find((a) => safeStr(a?.areaId || a?.id) === areaId);
  return safeStr(match?.name) || "";
}
